import { useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useGetUserInfo } from "src/api/auth";
import ExpirePlanModal from "src/components/expire-plan-modal";
import NoInternetConnection from "src/components/no-internet";
import { paths } from "src/pages/paths";
import type { TLoginVerifyResponse } from "src/type/auth";
import { UserStatusChoices } from "src/type/auth";
import axiosInstance from "src/utils/axios";

import { calculateDays } from "src/utils/format-time";
import type { ActionMapType, AuthStateType, AuthUserType } from "../../types";
import { AuthContext } from "./auth-context";
import { setSession } from "./utils";

// import { instance } from 'src/api/instance';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
	INITIAL = "INITIAL",
	LOGIN = "LOGIN",
	LOGOUT = "LOGOUT",
}

interface Payload {
	[Types.INITIAL]: {
		user: AuthUserType;
	};
	[Types.LOGIN]: {
		user: AuthUserType;
	};

	[Types.LOGOUT]: undefined;
}

//@ts-ignore: Type assertion is necessary here
type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
	user: null,
	loading: true,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
	if (action.type === Types.INITIAL) {
		return {
			loading: false,
			user: action.payload.user,
		};
	}
	if (action.type === Types.LOGIN) {
		return {
			...state,
			user: action.payload.user,
		};
	}

	return {
		...state,
		loading: false,
		user: null,
		authenticated: true,
	};
};

// ----------------------------------------------------------------------

const STORAGE_KEY = "accessToken";

interface Props {
	children: React.ReactNode;
}

export function AuthProvider({ children }: Props) {
	// const [showErrorComponent, setShowErrorComponent] = useState(true);
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [searchParams] = useSearchParams();
	const [showExpiredPlanModal, setShowExpiredPlanModal] = useState(false);
	const [state, dispatch] = useReducer(reducer, initialState);
	const defaultToken = searchParams.get("token");
	const referralCode = searchParams.get("referral");
	const accessToken = localStorage.getItem(STORAGE_KEY) ?? defaultToken;
	const carrierId = localStorage.getItem("carrierId");
	if (accessToken) {
		axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
	}
	if (carrierId) {
		axiosInstance.defaults.headers.common["HTTP-CURRENT-ID"] = carrierId;
	}

	useEffect(() => {
		if (referralCode) {
			localStorage.setItem("referralCode", referralCode);
		}
	}, [referralCode]);

	// LOGOUT
	const logout = useCallback(() => {
		localStorage.removeItem(STORAGE_KEY);
		localStorage.removeItem("carrierId");
		localStorage.removeItem("referralCode");
		localStorage.removeItem("isShownTour");
		localStorage.removeItem("isShowAddReferral");
		localStorage.removeItem("expire-plan-show");
		queryClient.clear();
		setSession(null);
		dispatch({
			type: Types.LOGOUT,
		});
		navigate(paths.auth.login);
		navigate(0);
	}, [queryClient]);

	axiosInstance.interceptors.response.use(
		(response) =>
			// Any status code that lie within the range of 2xx cause this function to trigger
			// Do something with response data
			response,
		(error: AxiosError) =>
			new Promise((_, reject) => {
				// if (error.message === "Network Error") {
				//   setShowErrorComponent(true);
				// }
				if (error.response?.status === 401) {
					// Don't redirect to login on public routes (e.g. /public/truck-visual)
					const isPublicRoute =
						typeof window !== "undefined" && window.location.pathname.startsWith("/public/");
					if (!isPublicRoute) {
						logout();
					} else {
						reject(error);
					}
				} else if ((error.response?.data as any)?.expired_plan) {
					setShowExpiredPlanModal(true);
				} else {
					reject(error);
				}
			}),
		// Any status codes that falls outside the range of 2xx cause this function to trigger
		// Do something with response error
	);

	const { data: userInfoData, error: userInfoDataError } = useGetUserInfo({
		enabled: !!accessToken,
	});

	useEffect(() => {
		if (userInfoData?.data) {
			dispatch({
				type: Types.INITIAL,
				payload: {
					user: userInfoData.data,
				},
			});
			if (userInfoData.data.current_carrier?.is_check_lock_trucks) {
				navigate(paths.dashboard.selectUnlockItems);
			}
			if (userInfoData.data.current_carrier && !userInfoData.data.current_carrier?.is_active) {
				navigate(paths.dashboard.disableCarrier);
			}
			if (!userInfoData.data.current_carrier) {
				navigate(`${paths.dashboard.createCarrier}?force=true`);
			}
			//TODO premium plan
			// if (
			//   !userInfoData.data.plan &&
			//   userInfoData.data.status === UserStatusChoices.ChoiceCreateTrip &&
			//   pathname !== "/dashboard/payment/"
			// ) {
			//   router.replace(paths.dashboard.premiumPlan);
			// }
			//TODO premium plan
			if (
				userInfoData.data.plan &&
				(calculateDays(userInfoData.data.plan.expired) === 0 ||
					calculateDays(userInfoData.data.plan.expired) < 0)
			) {
				const lastShowPopup = localStorage.getItem("expire-plan-show");
				if (
					!lastShowPopup ||
					lastShowPopup?.length === 0 ||
					calculateDays(lastShowPopup) === 0 ||
					(calculateDays(userInfoData.data.plan.expired) < 0 &&
						!window?.location?.pathname?.includes("/dashboard/payment/"))
				) {
					const todayDate = new Date();
					todayDate.setDate(todayDate.getDate() + 4);
					localStorage.setItem("expire-plan-show", todayDate.toDateString());
					setShowExpiredPlanModal(true);
				}
			}
			if (userInfoData.data.plan && calculateDays(userInfoData.data.plan.expired) === 0) {
				const isShowPopup = localStorage.getItem("plan-show") || "true";
				if (isShowPopup) {
					localStorage.setItem("plan-show", "false");
					setShowExpiredPlanModal(true);
				}
			}
			if (userInfoData.data.status !== UserStatusChoices.ChoiceCreateTrip.valueOf() || !carrierId) {
				if (userInfoData.data.current_carrier) {
					localStorage.setItem("carrierId", userInfoData.data.current_carrier.id.toString());
					axiosInstance.defaults.headers.common["HTTP-CURRENT-ID"] =
						userInfoData.data.current_carrier.id.toString();
				}
			}
		}
	}, [carrierId, userInfoData]);

	useEffect(() => {
		if (userInfoDataError && !window?.location?.pathname?.includes("/dashboard/payment/")) {
			toast.error(userInfoDataError.message);
		}
	}, [userInfoDataError]);

	useEffect(() => {
		if (!accessToken) {
			dispatch({
				type: Types.INITIAL,
				payload: {
					user: null,
				},
			});
		}
	}, [accessToken]);

	// LOGIN
	const login = useCallback(
		(userData: TLoginVerifyResponse) => {
			localStorage.setItem(STORAGE_KEY, userData.user.access_token);
			if (userData.user.current_carrier) {
				localStorage.setItem("carrierId", userData.user.current_carrier.id.toString());
			}
			dispatch({
				type: Types.LOGIN,
				payload: {
					user: {
						cellphone: userData.user.cellphone,
						firstname: userData.user.firstname,
						lastname: userData.user.lastname,
						wallet: 0,
						check_referral_code: false,
						default_length_symbol: "",
						default_size_symbol: "",
						email: null,
						status: userData.user.status,
						status_name: "",
						plan: null,
						user_type: 0,
						driver: {
							id: 0,
							license_number: "",
							default_truck_id: 0,
						},
						user_type_name: "",
						current_carrier: null,
						request_carrier: null,
						default_length: "",
						default_size: "",
					},
				},
			});
		},
		[dispatch],
	);

	// ----------------------------------------------------------------------

	const checkAuthenticated = state.user ? "authenticated" : "unauthenticated";

	const status = state.loading ? "loading" : checkAuthenticated;
	const memoizedValue = useMemo(
		() => ({
			user: state.user,
			loading: status === "loading",
			authenticated: status === "authenticated",
			unauthenticated: status === "unauthenticated",
			//
			login,
			logout,
		}),
		[login, logout, state.user, status],
	);

	return (
		<AuthContext.Provider value={memoizedValue}>
			{userInfoDataError?.message === "Network Error" ? (
				// && showErrorComponent
				<NoInternetConnection
				// setShowErrorComponent={setShowErrorComponent}
				/>
			) : (
				<>
					<ExpirePlanModal
						closeModal={() => setShowExpiredPlanModal(false)}
						open={showExpiredPlanModal}
					/>
					{children}
				</>
			)}
		</AuthContext.Provider>
	);
}
